import { useEffect, useRef } from 'react'

import { Extension } from '@tiptap/core'
import { EditorContent, useEditor, useEditorState } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import { TaskItem, TaskList } from '@tiptap/extension-list'
import { Placeholder } from '@tiptap/extensions'
import { Bold, Italic, List, ListChecks } from 'lucide-react'

import { docToMarkdown, markdownToDoc, type RichDocNode } from '@/lib/noteRichDoc'
import { cn } from '@/lib/utils'

/**
 * Markdown pairs a single newline with a tight in-paragraph line break and a
 * blank line with a new paragraph — but tiptap's stock Enter always splits
 * into a new paragraph (docToMarkdown then emits a blank-line gap for every
 * press), so there was no way to type the tight break at all. This makes
 * Enter mirror the markdown source: first press inserts a soft break inside
 * the paragraph (no gap); a second, immediate press turns that trailing soft
 * break into a real paragraph split (blank-line gap) instead of stacking two
 * breaks — i.e. "press Enter twice for a new paragraph", same as writing
 * markdown by hand. Scoped to plain paragraphs only: headings and list items
 * keep tiptap's default Enter behavior.
 */
const MarkdownStyleEnter = Extension.create({
  name: 'markdownStyleEnter',
  addKeyboardShortcuts() {
    return {
      Enter: () => {
        const { editor } = this
        const { $from, empty } = editor.state.selection
        const grandparent = $from.node($from.depth - 1)
        if (
          !empty ||
          $from.parent.type.name !== 'paragraph' ||
          grandparent?.type.name === 'listItem' ||
          grandparent?.type.name === 'taskItem'
        ) {
          return false
        }
        const nodeBefore = $from.nodeBefore
        if (nodeBefore?.type.name === 'hardBreak') {
          return editor
            .chain()
            .command(({ tr }) => {
              tr.delete($from.pos - nodeBefore.nodeSize, $from.pos)
              return true
            })
            .splitBlock()
            .run()
        }
        return editor.commands.setHardBreak()
      },
    }
  },
})

export interface RichMarkdownEditorProps {
  /**
   * Initial markdown. Read once when the editor mounts — remount (key) to
   * load different content. Markdown is also what comes back out: the editor
   * never leaks tiptap JSON to callers.
   */
  initialValue?: string
  /** Fires with the serialized markdown after every document change. */
  onChange?: (markdown: string) => void
  placeholder?: string
  autoFocus?: boolean
  ariaLabel?: string
  /** Extra classes for the outer frame (borders, background). */
  className?: string
  /** Extra classes for the editable area itself (min-height, text size). */
  contentClassName?: string
}

/**
 * The rich editing surface for notes and status reasons: markdown in,
 * markdown out, with the same tiny subset lib/noteMarkdown.ts renders —
 * bold, italic, bullet lists, checklists, links (pasted/autolinked),
 * headings. The toolbar exists because techs are on tablets: keyboard
 * shortcuts still work, but they can't be the only way in.
 */
export function RichMarkdownEditor({
  initialValue = '',
  onChange,
  placeholder,
  autoFocus = false,
  ariaLabel,
  className,
  contentClassName,
}: RichMarkdownEditorProps) {
  // The latest onChange without making it an editor dependency — recreating
  // the editor on every parent render would drop selection and undo history.
  const onChangeRef = useRef(onChange)
  useEffect(() => {
    onChangeRef.current = onChange
  }, [onChange])

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        // Everything the note subset can't store stays off, so nothing the
        // user produces here is lost on save.
        blockquote: false,
        code: false,
        codeBlock: false,
        horizontalRule: false,
        strike: false,
        underline: false,
        orderedList: false,
        heading: { levels: [1, 2, 3] },
        link: {
          openOnClick: false,
          autolink: true,
          defaultProtocol: 'https',
        },
      }),
      TaskList,
      TaskItem.configure({ nested: false }),
      MarkdownStyleEnter,
      ...(placeholder ? [Placeholder.configure({ placeholder })] : []),
    ],
    content: markdownToDoc(initialValue),
    autofocus: autoFocus ? 'end' : false,
    onUpdate: ({ editor: current }) => {
      onChangeRef.current?.(docToMarkdown(current.getJSON() as RichDocNode))
    },
    editorProps: {
      attributes: {
        class: cn(
          'jc-no-ios-zoom min-h-14 px-2.5 py-2 text-sm text-slate-900 outline-none',
          contentClassName,
        ),
        ...(ariaLabel ? { 'aria-label': ariaLabel } : {}),
      },
    },
  })

  const active = useEditorState({
    editor,
    selector: ({ editor: current }) => ({
      bold: current?.isActive('bold') ?? false,
      italic: current?.isActive('italic') ?? false,
      bulletList: current?.isActive('bulletList') ?? false,
      taskList: current?.isActive('taskList') ?? false,
    }),
  })

  const toolbarButton = (
    label: string,
    isActive: boolean,
    Icon: typeof Bold,
    run: () => void,
  ) => (
    <button
      type="button"
      aria-label={label}
      aria-pressed={isActive}
      title={label}
      // Mousedown, not click, so the button press never blurs the editor.
      onMouseDown={(event) => {
        event.preventDefault()
        run()
      }}
      className={cn(
        'inline-flex h-7 w-7 items-center justify-center rounded-md',
        isActive
          ? 'bg-slate-200 text-slate-900'
          : 'text-slate-500 hover:bg-slate-100 hover:text-slate-700',
      )}
    >
      <Icon className="h-3.5 w-3.5" aria-hidden="true" />
    </button>
  )

  return (
    <div
      className={cn(
        'jc-rich-editor rounded-lg border border-slate-300 bg-white focus-within:border-slate-500',
        className,
      )}
    >
      <EditorContent editor={editor} />
      <div className="flex items-center gap-0.5 border-t border-slate-100 px-1.5 py-1">
        {toolbarButton('Bold', active?.bold ?? false, Bold, () =>
          editor?.chain().focus().toggleBold().run(),
        )}
        {toolbarButton('Italic', active?.italic ?? false, Italic, () =>
          editor?.chain().focus().toggleItalic().run(),
        )}
        {toolbarButton('Bullet list', active?.bulletList ?? false, List, () =>
          editor?.chain().focus().toggleBulletList().run(),
        )}
        {toolbarButton('Checklist', active?.taskList ?? false, ListChecks, () =>
          editor?.chain().focus().toggleTaskList().run(),
        )}
      </div>
    </div>
  )
}
